import { safeGetJSON, safeSetJSON } from "./safeStorage";

export type MoodId = "focus" | "gym" | "worship" | "late-night" | "victory";

export interface ArtistImage {
  url: string;
}

export interface SpotifyAlbumSummary {
  id?: string;
  name: string;
  images?: ArtistImage[];
  release_date: string;
  album_type?: string;
  album_group?: string;
  total_tracks?: number;
  external_urls?: { spotify?: string };
  appleMusicUrl?: string;
}

export interface SpotifyAlbumTrack {
  id?: string;
  name: string;
  track_number?: number;
  duration_ms?: number;
  preview_url?: string | null;
  artists?: Array<{ name: string }>;
  external_urls?: { spotify?: string };
}

export interface FanTrack {
  id: string;
  name: string;
  artistName: string;
  albumName: string;
  albumId?: string;
  albumImage?: string;
  releaseDate: string;
  releaseType: "single" | "ep" | "album";
  durationMs?: number;
  previewUrl?: string | null;
  spotifyUrl?: string;
  appleMusicUrl?: string;
  path: string;
  trackNumber: number;
}

export interface MoodDefinition {
  id: MoodId;
  label: string;
  summary: string;
  icon: "sunrise" | "zap" | "sparkles" | "moon" | "archive";
  keywords: string[];
  recency: number;
  duration: number;
  deepCut: number;
}

export interface FanPreferences {
  selectedMood?: MoodId;
  dailyPickDate?: string;
  dailyPickId?: string;
  favoriteTrackIds: string[];
  dismissedAnniversaryIds: string[];
  featureOpens: Record<string, number>;
  firstVisitAt: string;
  lastVisitAt: string;
  visitCount: number;
}

export interface MoodScorePoint {
  track: FanTrack;
  score: number;
}

export interface MoodPlaylistOptions {
  minTracks?: number;
  maxTracks?: number;
}

export interface TimelineStat {
  year: string;
  count: number;
  share: number;
}

export interface ReleaseMixStat {
  type: FanTrack["releaseType"];
  count: number;
  share: number;
}

export const FAN_MOODS: MoodDefinition[] = [
  {
    id: "focus",
    label: "Morning",
    summary: "Wake-up picks balanced for a clean early run through this artist's catalog.",
    icon: "sunrise",
    keywords: ["music", "song", "track", "release", "album", "morning", "wake"],
    recency: 0.5,
    duration: 0.62,
    deepCut: 0.28,
  },
  {
    id: "gym",
    label: "Pulse",
    summary: "Higher-motion picks shaped by this artist's catalog.",
    icon: "zap",
    keywords: ["music", "song", "track", "release", "album"],
    recency: 0.55,
    duration: 0.45,
    deepCut: 0.25,
  },
  {
    id: "worship",
    label: "Signal",
    summary: "Most repeated catalog signals from this artist.",
    icon: "sparkles",
    keywords: ["music", "song", "track", "release", "album"],
    recency: 0.4,
    duration: 0.65,
    deepCut: 0.35,
  },
  {
    id: "late-night",
    label: "After Hours",
    summary: "Slower and deeper catalog picks from this artist.",
    icon: "moon",
    keywords: ["music", "song", "track", "release", "album"],
    recency: 0.3,
    duration: 0.55,
    deepCut: 0.85,
  },
  {
    id: "victory",
    label: "Vault",
    summary: "Older and deeper catalog picks from this artist.",
    icon: "archive",
    keywords: ["music", "song", "track", "release", "album"],
    recency: 0.7,
    duration: 0.45,
    deepCut: 0.2,
  },
];

const FAN_MOOD_STOP_WORDS = new Set([
  "and",
  "are",
  "album",
  "feat",
  "featuring",
  "for",
  "from",
  "into",
  "music",
  "part",
  "pt",
  "release",
  "remix",
  "song",
  "the",
  "this",
  "track",
  "version",
  "with",
  "you",
  "your",
]);

const normalize = (value = "") => value.toLowerCase().replace(/[^a-z0-9\s]/g, " ");

const safeStringWithHyphens = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9 ]/gi, "")
    .replace(/ /g, "-");

const normalizeTrackKey = (track: FanTrack) => normalize(track.name).replace(/\s+/g, " ").trim();

export const isSpotifyTrackId = (id?: string) => Boolean(id && /^[A-Za-z0-9]{22}$/.test(id));

const daysBetween = (left: Date, right: Date) =>
  Math.abs(left.getTime() - right.getTime()) / 86400000;

const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const titleCase = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");

interface CatalogMoodTerm {
  term: string;
  count: number;
  recentWeight: number;
  briskWeight: number;
  deepWeight: number;
  durationWeight: number;
}

const getCatalogMoodTerms = (tracks: FanTrack[], artistName: string): CatalogMoodTerm[] => {
  const terms = new Map<string, CatalogMoodTerm>();
  const artistWords = new Set(normalize(artistName).split(/\s+/).filter(Boolean));
  const newestTime = Math.max(
    0,
    ...tracks.map((track) => new Date(track.releaseDate).getTime()).filter(Number.isFinite)
  );
  const oldestTime = Math.min(
    newestTime,
    ...tracks.map((track) => new Date(track.releaseDate).getTime()).filter(Number.isFinite)
  );
  const releaseSpan = Math.max(1, newestTime - oldestTime);

  tracks.forEach((track, index) => {
    const releaseTime = new Date(track.releaseDate).getTime();
    const recency = Number.isFinite(releaseTime) ? (releaseTime - oldestTime) / releaseSpan : 0.5;
    const deepness = tracks.length <= 1 ? 0.5 : index / Math.max(1, tracks.length - 1);
    const duration = track.durationMs ? Math.min(1, track.durationMs / 300000) : 0.5;
    const brisk = 1 - duration;
    const words = [
      ...normalize(track.name).split(/\s+/).map((word) => ({ word, weight: 2.4 })),
      ...normalize(track.albumName).split(/\s+/).map((word) => ({ word, weight: 1.35 })),
    ];

    words.forEach(({ word, weight }) => {
      if (word.length < 3 || FAN_MOOD_STOP_WORDS.has(word) || artistWords.has(word)) return;
      const existing =
        terms.get(word) || {
          term: word,
          count: 0,
          recentWeight: 0,
          briskWeight: 0,
          deepWeight: 0,
          durationWeight: 0,
        };
      existing.count += weight;
      existing.recentWeight += weight * (0.35 + recency);
      existing.briskWeight += weight * (0.35 + brisk);
      existing.deepWeight += weight * (0.35 + deepness);
      existing.durationWeight += weight * (0.35 + duration);
      terms.set(word, existing);
    });
  });

  return [...terms.values()].sort(
    (left, right) => right.count - left.count || left.term.localeCompare(right.term)
  );
};

const pickMoodTerms = (
  terms: CatalogMoodTerm[],
  key: keyof Omit<CatalogMoodTerm, "term">,
  fallback: string[]
) => {
  const picked = [...terms]
    .sort((left, right) => Number(right[key]) - Number(left[key]) || right.count - left.count)
    .map((term) => term.term);
  return [...new Set([...picked, ...fallback])].slice(0, 8);
};

const makeMoodLabel = (
  terms: string[],
  suffix: string,
  fallback: string,
  usedLabels: Set<string>,
  usedTerms: Set<string>
) => {
  const termCandidates = terms
    .slice(0, 4)
    .filter((term) => !usedTerms.has(term))
    .map((term) => ({ term, label: `${titleCase(term)} ${suffix}`.trim() }));
  const fallbackCandidates = terms
    .slice(0, 4)
    .map((term) => ({ term, label: `${titleCase(term)} ${suffix}`.trim() }))
    .concat({ term: "", label: fallback });
  const candidate =
    termCandidates.find((item) => !usedLabels.has(item.label.toLowerCase())) ||
    fallbackCandidates.find((item) => !usedLabels.has(item.label.toLowerCase())) ||
    { term: "", label: fallback };
  const label = candidate.label;
  usedLabels.add(label.toLowerCase());
  if (candidate.term) usedTerms.add(candidate.term);
  return label;
};

export const getDynamicFanMoods = (tracks: FanTrack[], artistName: string): MoodDefinition[] => {
  const terms = getCatalogMoodTerms(tracks, artistName);
  const usedLabels = new Set<string>();
  const usedTerms = new Set<string>();
  const fallbackTerms = ["catalog", "release"];
  const recentTerms = pickMoodTerms(terms, "recentWeight", fallbackTerms);
  const pulseTerms = pickMoodTerms(terms, "briskWeight", recentTerms);
  const signalTerms = pickMoodTerms(terms, "count", recentTerms);
  const lateTerms = pickMoodTerms(terms, "durationWeight", signalTerms);
  const vaultTerms = pickMoodTerms(terms, "deepWeight", signalTerms);

  return [
    {
      ...FAN_MOODS[0],
      label: makeMoodLabel(recentTerms, "Rise", "Morning Rise", usedLabels, usedTerms),
      summary: `Early ${artistName} picks balanced by catalog words, release dates, and track length from ${recentTerms.slice(0, 3).map(titleCase).join(", ")}.`,
      keywords: recentTerms,
      recency: 0.62,
      duration: 0.62,
      deepCut: 0.24,
    },
    {
      ...FAN_MOODS[1],
      label: makeMoodLabel(pulseTerms, "Pulse", "Catalog Pulse", usedLabels, usedTerms),
      summary: `Higher-motion ${artistName} tracks with ${pulseTerms.slice(0, 3).map(titleCase).join(", ")} signals.`,
      keywords: pulseTerms,
      recency: 0.5,
      duration: 0.42,
      deepCut: 0.24,
    },
    {
      ...FAN_MOODS[2],
      label: makeMoodLabel(signalTerms, "Signal", "Catalog Signal", usedLabels, usedTerms),
      summary: `The strongest repeated ${artistName} catalog words: ${signalTerms.slice(0, 3).map(titleCase).join(", ")}.`,
      keywords: signalTerms,
      recency: 0.42,
      duration: 0.62,
      deepCut: 0.34,
    },
    {
      ...FAN_MOODS[3],
      label: makeMoodLabel(lateTerms, "After", "After Hours", usedLabels, usedTerms),
      summary: `Longer or lower-motion ${artistName} picks led by ${lateTerms.slice(0, 3).map(titleCase).join(", ")}.`,
      keywords: lateTerms,
      recency: 0.3,
      duration: 0.78,
      deepCut: 0.68,
    },
    {
      ...FAN_MOODS[4],
      label: makeMoodLabel(vaultTerms, "Vault", "Deep Catalog", usedLabels, usedTerms),
      summary: `Deeper ${artistName} catalog paths surfaced through ${vaultTerms.slice(0, 3).map(titleCase).join(", ")}.`,
      keywords: vaultTerms,
      recency: 0.22,
      duration: 0.54,
      deepCut: 0.9,
    },
  ];
};

const resolveMood = (moodId: MoodId, moods: MoodDefinition[] = FAN_MOODS) =>
  moods.find((item) => item.id === moodId) || moods[0] || FAN_MOODS[0];

export const getReleaseType = (totalTracks = 1): FanTrack["releaseType"] => {
  if (totalTracks >= 7) return "album";
  if (totalTracks >= 3) return "ep";
  return "single";
};

export const createDefaultFanPreferences = (): FanPreferences => {
  const now = new Date().toISOString();
  return {
    favoriteTrackIds: [],
    dismissedAnniversaryIds: [],
    featureOpens: {},
    firstVisitAt: now,
    lastVisitAt: now,
    visitCount: 1,
  };
};

export const getFanStorageKey = (artistName: string) => {
  return `truevined-fan-hub:${normalize(artistName).replace(/\s+/g, "-") || "artist"}`;
};

export const FAN_PREFERENCE_LIMITS = {
  favoriteTrackIds: 200,
  dismissedAnniversaryIds: 100,
  featureOpens: 16,
  idLength: 64,
} as const;

const boundedIds = (value: unknown, max: number): string[] => {
  if (!Array.isArray(value)) return [];
  const ids = value.filter(
    (item): item is string =>
      typeof item === "string" && item.length > 0 && item.length <= FAN_PREFERENCE_LIMITS.idLength
  );
  return Array.from(new Set(ids)).slice(-max);
};

const boundedFeatureOpens = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(
        ([key, count]) =>
          key.length <= FAN_PREFERENCE_LIMITS.idLength &&
          typeof count === "number" &&
          Number.isFinite(count) &&
          count >= 0
      )
      .slice(0, FAN_PREFERENCE_LIMITS.featureOpens)
      .map(([key, count]) => [key, Math.min(Math.floor(count as number), 1_000_000)])
  );
};

const validIsoDate = (value: unknown, fallback: string) =>
  typeof value === "string" && value.length <= 40 && !Number.isNaN(Date.parse(value))
    ? value
    : fallback;

export const sanitizeFanPreferences = (input: unknown): FanPreferences => {
  const defaults = createDefaultFanPreferences();
  if (!input || typeof input !== "object" || Array.isArray(input)) return defaults;
  const source = input as Partial<Record<keyof FanPreferences, unknown>>;
  const sanitized: FanPreferences = {
    favoriteTrackIds: boundedIds(source.favoriteTrackIds, FAN_PREFERENCE_LIMITS.favoriteTrackIds),
    dismissedAnniversaryIds: boundedIds(
      source.dismissedAnniversaryIds,
      FAN_PREFERENCE_LIMITS.dismissedAnniversaryIds
    ),
    featureOpens: boundedFeatureOpens(source.featureOpens),
    firstVisitAt: validIsoDate(source.firstVisitAt, defaults.firstVisitAt),
    lastVisitAt: validIsoDate(source.lastVisitAt, defaults.lastVisitAt),
    visitCount:
      typeof source.visitCount === "number" && Number.isFinite(source.visitCount) && source.visitCount > 0
        ? Math.min(Math.floor(source.visitCount), 1_000_000)
        : defaults.visitCount,
  };
  if (FAN_MOODS.some((mood) => mood.id === source.selectedMood)) {
    sanitized.selectedMood = source.selectedMood as MoodId;
  }
  if (typeof source.dailyPickDate === "string" && source.dailyPickDate.length <= 40) {
    sanitized.dailyPickDate = source.dailyPickDate;
  }
  if (
    typeof source.dailyPickId === "string" &&
    source.dailyPickId.length <= FAN_PREFERENCE_LIMITS.idLength
  ) {
    sanitized.dailyPickId = source.dailyPickId;
  }
  return sanitized;
};

export const loadFanPreferences = (artistName: string): FanPreferences => {
  if (typeof window === "undefined") return createDefaultFanPreferences();

  const key = getFanStorageKey(artistName);
  const parsed = safeGetJSON<FanPreferences>(key);
  if (!parsed) {
    const created = createDefaultFanPreferences();
    safeSetJSON(key, created);
    return created;
  }

  const sanitized = sanitizeFanPreferences(parsed);
  return {
    ...sanitized,
    lastVisitAt: new Date().toISOString(),
    visitCount: Math.min(sanitized.visitCount + 1, 1_000_000),
  };
};

export const saveFanPreferences = (artistName: string, preferences: FanPreferences): boolean => {
  if (typeof window === "undefined") return false;
  return safeSetJSON(getFanStorageKey(artistName), sanitizeFanPreferences(preferences));
};

export const buildFanTracks = (
  artistName: string,
  albums: SpotifyAlbumSummary[],
  albumDetails: Record<
    string,
    { tracks?: { items?: SpotifyAlbumTrack[] }; total_tracks?: number; images?: ArtistImage[] }
  >
): FanTrack[] => {
  return albums.flatMap((album) => {
    const detail = albumDetails[album.id || album.name];
    const tracks = detail?.tracks?.items || [];
    const totalTracks = detail?.total_tracks || album.total_tracks || tracks.length || 1;
    const releaseType = getReleaseType(totalTracks);
    const albumImage = detail?.images?.[0]?.url || album.images?.[0]?.url;

    if (tracks.length === 0) {
      return [
        {
          id: album.id || album.name,
          name: album.name,
          artistName,
          albumName: album.name,
          albumId: album.id,
          albumImage,
          releaseDate: album.release_date,
          releaseType,
          path: `/${releaseType === "single" ? "songs" : "albums"}/${safeStringWithHyphens(album.name)}`,
          trackNumber: 1,
          spotifyUrl: album.external_urls?.spotify,
          appleMusicUrl: album.appleMusicUrl,
        },
      ];
    }

    return tracks.map((track, index) => ({
      id: track.id || `${album.id || album.name}-${index}`,
      name: track.name,
      artistName,
      albumName: album.name,
      albumId: album.id,
      albumImage,
      releaseDate: album.release_date,
      releaseType,
      durationMs: track.duration_ms,
      previewUrl: track.preview_url,
      spotifyUrl: track.external_urls?.spotify || album.external_urls?.spotify,
      appleMusicUrl: album.appleMusicUrl,
      path: `/${releaseType === "single" ? "songs" : "albums"}/${safeStringWithHyphens(releaseType === "single" ? track.name : album.name)}`,
      trackNumber: track.track_number || index + 1,
    }));
  });
};

const keywordScore = (track: FanTrack, mood: MoodDefinition) => {
  const text = normalize(`${track.name} ${track.albumName}`);
  const hits = mood.keywords.filter((keyword) => text.includes(keyword)).length;
  return Math.min(1, hits / Math.max(2, mood.keywords.length * 0.28));
};

const durationScore = (track: FanTrack, mood: MoodDefinition) => {
  if (!track.durationMs) return 0.5;
  const minutes = track.durationMs / 60000;
  const ideal = mood.duration > 0.7 ? 2.4 : mood.duration > 0.5 ? 3.1 : 2.8;
  return Math.max(0, 1 - Math.abs(minutes - ideal) / 4);
};

const recencyScore = (track: FanTrack, mood: MoodDefinition) => {
  const daysOld = daysBetween(new Date(track.releaseDate), new Date());
  const score = Math.max(0, 1 - daysOld / 1460);
  return mood.recency * score + (1 - mood.recency) * (1 - score * 0.65);
};

const deepCutScore = (track: FanTrack, tracks: FanTrack[], mood: MoodDefinition) => {
  const index = tracks.findIndex((candidate) => candidate.id === track.id);
  const catalogPosition = tracks.length <= 1 ? 0 : index / (tracks.length - 1);
  const albumDepth = track.trackNumber > 3 ? 0.7 : 0.25;
  return (
    mood.deepCut * Math.max(catalogPosition, albumDepth) +
    (1 - mood.deepCut) * (1 - catalogPosition * 0.5)
  );
};

export const scoreTrackForMood = (track: FanTrack, tracks: FanTrack[], mood: MoodDefinition) => {
  return (
    keywordScore(track, mood) * 0.46 +
    recencyScore(track, mood) * 0.22 +
    durationScore(track, mood) * 0.16 +
    deepCutScore(track, tracks, mood) * 0.16
  );
};

export const getMoodTracks = (
  tracks: FanTrack[],
  moodId: MoodId,
  limit = 8,
  moods: MoodDefinition[] = FAN_MOODS
): FanTrack[] => {
  const mood = resolveMood(moodId, moods);
  const seen = new Set<string>();
  return [...tracks]
    .map((track) => ({ track, score: scoreTrackForMood(track, tracks, mood) }))
    .sort(
      (left, right) => right.score - left.score || left.track.name.localeCompare(right.track.name)
    )
    .filter(({ track }) => {
      const key = normalizeTrackKey(track);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit)
    .map(({ track }) => track);
};

export const getMoodScorePoints = (
  tracks: FanTrack[],
  moodId: MoodId,
  limit = 8,
  moods: MoodDefinition[] = FAN_MOODS
): MoodScorePoint[] => {
  const mood = resolveMood(moodId, moods);
  const seen = new Set<string>();
  return [...tracks]
    .map((track) => ({ track, score: scoreTrackForMood(track, tracks, mood) }))
    .sort(
      (left, right) => right.score - left.score || left.track.name.localeCompare(right.track.name)
    )
    .filter(({ track }) => {
      const key = normalizeTrackKey(track);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
};

export const getMoodPlaylistTracks = (
  tracks: FanTrack[],
  moodId: MoodId,
  { minTracks = 10, maxTracks = 20 }: MoodPlaylistOptions = {},
  moods: MoodDefinition[] = FAN_MOODS
): FanTrack[] => {
  const mood = resolveMood(moodId, moods);
  const seen = new Set<string>();
  const ranked = [...tracks]
    .map((track, index) => ({ track, index }))
    .filter(({ track }) => isSpotifyTrackId(track.id))
    .map(({ track, index }) => ({
      track,
      index,
      score: scoreTrackForMood(track, tracks, mood),
    }))
    .sort((left, right) => right.score - left.score || left.track.name.localeCompare(right.track.name))
    .filter(({ track }) => {
      const key = normalizeTrackKey(track);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  if (ranked.length <= 1) return ranked.map(({ track }) => track);

  const targetCount = Math.min(
    ranked.length,
    Math.max(
      Math.min(minTracks, ranked.length),
      Math.min(maxTracks, Math.ceil(ranked.length * 0.72))
    )
  );
  return ranked
    .slice(0, targetCount)
    .sort((left, right) => left.index - right.index || left.track.trackNumber - right.track.trackNumber)
    .map(({ track }) => track);
};

export const getDailyPick = (
  tracks: FanTrack[],
  artistName: string,
  date = new Date()
): FanTrack | null => {
  if (tracks.length === 0) return null;
  const dayKey = date.toISOString().slice(0, 10);
  const sorted = [...tracks].sort(
    (left, right) => new Date(right.releaseDate).getTime() - new Date(left.releaseDate).getTime()
  );
  const weightedPool = sorted.flatMap((track, index) =>
    Array.from({ length: Math.max(1, 6 - Math.min(index, 5)) }, () => track)
  );
  return weightedPool[hashString(`${artistName}:${dayKey}`) % weightedPool.length];
};

export const getDeepCut = (
  tracks: FanTrack[],
  artistName: string,
  seed = new Date().toISOString().slice(0, 10)
): FanTrack | null => {
  if (tracks.length === 0) return null;
  const olderTracks = [...tracks]
    .sort(
      (left, right) => new Date(left.releaseDate).getTime() - new Date(right.releaseDate).getTime()
    )
    .slice(0, Math.max(1, Math.ceil(tracks.length * 0.65)));
  return olderTracks[hashString(`${artistName}:deep:${seed}`) % olderTracks.length];
};

export const getTimeline = (albums: SpotifyAlbumSummary[]) => {
  const grouped = albums.reduce<Record<string, SpotifyAlbumSummary[]>>((acc, album) => {
    const year = String(new Date(album.release_date).getFullYear());
    acc[year] = acc[year] || [];
    acc[year].push(album);
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([left], [right]) => Number(right) - Number(left))
    .map(([year, releases]) => ({
      year,
      releases: releases.sort(
        (left, right) =>
          new Date(right.release_date).getTime() - new Date(left.release_date).getTime()
      ),
    }));
};

export const getTimelineStats = (albums: SpotifyAlbumSummary[]): TimelineStat[] => {
  const timeline = getTimeline(albums);
  const total = timeline.reduce((sum, group) => sum + group.releases.length, 0);
  return timeline.map((group) => ({
    year: group.year,
    count: group.releases.length,
    share: total > 0 ? group.releases.length / total : 0,
  }));
};

export const getReleaseMixStats = (albums: SpotifyAlbumSummary[]): ReleaseMixStat[] => {
  const counts = albums.reduce<Record<FanTrack["releaseType"], number>>(
    (acc, album) => {
      const type = getReleaseType(album.total_tracks || 1);
      acc[type] += 1;
      return acc;
    },
    { single: 0, ep: 0, album: 0 }
  );
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  return (["single", "ep", "album"] as FanTrack["releaseType"][]).map((type) => ({
    type,
    count: counts[type],
    share: total > 0 ? counts[type] / total : 0,
  }));
};

export const getAnniversaryReleases = (albums: SpotifyAlbumSummary[], now = new Date()) => {
  return albums
    .map((album) => {
      const releaseDate = new Date(album.release_date);
      const thisYear = new Date(now.getFullYear(), releaseDate.getMonth(), releaseDate.getDate());
      const deltaDays = Math.round((thisYear.getTime() - now.getTime()) / 86400000);
      const daysUntil = deltaDays < -14 ? deltaDays + 365 : deltaDays;
      return { album, daysUntil, years: now.getFullYear() - releaseDate.getFullYear() };
    })
    .filter((item) => item.years > 0 && item.daysUntil >= -14 && item.daysUntil <= 30)
    .sort((left, right) => Math.abs(left.daysUntil) - Math.abs(right.daysUntil));
};

export const getPassportStats = (
  tracks: FanTrack[],
  preferences: FanPreferences,
  moods: MoodDefinition[] = FAN_MOODS
) => {
  const releases = new Set(tracks.map((track) => track.albumId || track.albumName));
  const years = tracks.map((track) => new Date(track.releaseDate).getFullYear()).filter(Boolean);
  const favoriteTracks = tracks.filter((track) => preferences.favoriteTrackIds.includes(track.id));
  return {
    visitCount: preferences.visitCount,
    favoriteCount: favoriteTracks.length,
    releaseCount: releases.size,
    trackCount: tracks.length,
    firstYear: years.length ? Math.min(...years) : null,
    latestYear: years.length ? Math.max(...years) : null,
    topMood: preferences.selectedMood
      ? resolveMood(preferences.selectedMood, moods).label
      : "Not picked yet",
  };
};
